"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Bell, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ui";
import { useAuth, useVertical } from "@/contexts";

export function Sidebar() {
  const pathname = usePathname();
  const { user, logout } = useAuth();
  const { config } = useVertical();

  const { branding, navigation } = config;

  return (
    <aside className="fixed left-0 top-0 z-40 flex h-screen w-64 flex-col bg-sidebar">
      {/* Logo */}
      <div className="flex h-20 items-center px-6">
        <Link href="/dashboard" className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent">
            <span className="font-outfit text-lg font-medium text-white">
              {branding.logoInitial}
            </span>
          </div>
          <div>
            <h1 className="font-outfit text-lg tracking-wide text-sidebar-foreground">
              {branding.appName}
            </h1>
            <p className="text-xs text-sidebar-foreground/50">
              {branding.subtitle}
            </p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4">
        {navigation.map((item) => {
          const isActive =
            pathname === item.href || pathname.startsWith(`${item.href}/`);
          return (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200",
                isActive
                  ? "bg-sidebar-accent/10 text-sidebar-accent"
                  : "text-sidebar-foreground/70 hover:bg-white/5 hover:text-sidebar-foreground"
              )}
            >
              <item.icon
                className={cn(
                  "h-5 w-5 transition-colors",
                  isActive
                    ? "text-sidebar-accent"
                    : "text-sidebar-foreground/50 group-hover:text-sidebar-foreground/70"
                )}
                strokeWidth={1.5}
              />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-white/10 p-4">
        {/* Notifications */}
        <button className="mb-4 flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-sidebar-foreground/70 transition-all duration-200 hover:bg-white/5 hover:text-sidebar-foreground">
          <div className="relative">
            <Bell className="h-5 w-5" strokeWidth={1.5} />
            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[10px] font-medium text-white">
              3
            </span>
          </div>
          Notificações
        </button>

        {/* User profile */}
        <div className="flex items-center gap-3 rounded-xl bg-white/5 p-3">
          <Avatar name={user?.name || "Usuário"} size="md" />
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium text-sidebar-foreground">
              {user?.name || "Usuário"}
            </p>
            <p className="truncate text-xs text-sidebar-foreground/50">
              {user?.role || ""}
            </p>
          </div>
          <button
            onClick={logout}
            className="rounded-lg p-2 text-sidebar-foreground/50 transition-colors hover:bg-white/10 hover:text-sidebar-foreground"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </aside>
  );
}
